import { CommonModule } from '@angular/common';
import { Component, computed, inject, Input, output, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { InputFieldComponent } from '../../../../shared/components/ui/input-field/input-field.component';
import { CtaButtonComponent } from '../../../../shared/components/ui/cta-button/cta-button.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { getTakeUntilDestroyed } from '../../../../core/helpers/take-until-destroy.helper';
import { AppToasterService } from '../../../../core/services/toaster.service';

@Component({
  selector: 'app-update-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    InputFieldComponent,
    CtaButtonComponent,
    ParentButtonComponent,
  ],
  templateUrl: './update-password.component.html',
  styleUrl: './update-password.component.scss',
})
export class UpdatePasswordComponent {
  #authService = inject(AuthService);
  #router = inject(Router);
  #route = inject(ActivatedRoute);
  #toaster = inject(AppToasterService);
  #fb = inject(FormBuilder);
  #translate = inject(TranslateService);

  private takeUntilDestroyed = getTakeUntilDestroyed();
  #embedded = signal(false);
  #initialUserName = signal<string | null>(null);
  #layout = signal<string | undefined>(undefined);

  readonly isLoading = computed(() => this.#authService.updatePassword.isPending());
  readonly embedded = computed(() => this.#embedded());
  readonly isParentLayout = computed(
    () => this.#layout() === 'parent' || this.#layout() === 'admin',
  );
  readonly form = this.#fb.nonNullable.group({
    userName: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9._]+$/)]],
    code: ['', [Validators.required]],
    password: ['', [Validators.required]],
    confirmPassword: ['', [Validators.required]],
  });

  readonly userNameValidation = signal<Record<string, string>>({
    required: '',
    userName: '',
  });
  readonly codeValidation = signal<Record<string, string>>({
    required: '',
  });
  readonly passwordValidation = signal<Record<string, string>>({
    required: '',
  });
  readonly confirmPasswordValidation = signal<Record<string, string>>({
    required: '',
    mismatch: '',
  });
  updated = output<void>();
  back = output<void>();

  @Input() set isEmbedded(value: boolean) {
    this.#embedded.set(value);
  }

  @Input() set initialUserName(value: string | null) {
    this.#initialUserName.set(value);
    if (value) {
      this.form.patchValue({ userName: value });
    }
  }

  @Input() set layout(value: string | undefined) {
    this.#layout.set(value);
  }

  ngOnInit(): void {
    const userName = this.#initialUserName() || this.#route.snapshot.queryParamMap.get('userName');
    if (userName) {
      this.form.patchValue({ userName });
    }

    this.#translate
      .get([
        'app.auth.requiredUserName',
        'app.auth.validUserName',
        'app.auth.requiredCode',
        'app.auth.requiredPassword',
        'app.auth.requiredConfirmPassword',
        'app.auth.passwordMismatch',
      ])
      .pipe(this.takeUntilDestroyed())
      .subscribe((translations) => {
        this.userNameValidation.set({
          required: translations['app.auth.requiredUserName'],
          userName: translations['app.auth.validUserName'],
        });
        this.codeValidation.set({
          required: translations['app.auth.requiredCode'],
        });
        this.passwordValidation.set({
          required: translations['app.auth.requiredPassword'],
        });
        this.confirmPasswordValidation.set({
          required: translations['app.auth.requiredConfirmPassword'],
          mismatch: translations['app.auth.passwordMismatch'],
        });
      });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { userName, code, password, confirmPassword } = this.form.getRawValue();
    if (password !== confirmPassword) {
      this.form.controls.confirmPassword.setErrors({ mismatch: true });
      this.form.controls.confirmPassword.markAsTouched();
      return;
    }

    this.#authService.updatePassword
      .mutateAsync({ userName, code, password })
      .then(() => {
        this.#toaster.success(
          this.#translate.instant('app.auth.passwordUpdatedMessage'),
        );
        if (this.embedded()) {
          this.updated.emit();
          return;
        }
        this.#router.navigate(['/auth']);
      });
  }

  goToSignin(): void {
    if (this.embedded()) {
      this.back.emit();
      return;
    }
    this.#router.navigate(['/auth']);
  }
}
